import React, { useContext, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/Layout';
import { AuthContext } from '../context/AuthContext';
import API_BASE_URL from '../config/api';
import Pagination from '../components/Pagination';
import usePagination from '../hooks/usePagination';
import './Access.css';

const formatDateTime = (value) => {
  if (!value) return 'Never';
  try {
    return new Date(value).toLocaleString();
  } catch {
    return String(value);
  }
};

const Employees = () => {
  const { hasPermission, user } = useContext(AuthContext);
  const navigate = useNavigate();

  const [employees, setEmployees] = useState([]);
  const [roles, setRoles] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [busyId, setBusyId] = useState(null);

  const canCreate = hasPermission('employees.create');
  const canUpdate = hasPermission('employees.update');
  const canDelete = hasPermission('employees.delete');
  const canSeeRoles = hasPermission('roles.view');

  const fetchEmployees = async () => {
    try {
      setLoading(true);
      setError('');
      const params = new URLSearchParams();
      if (search.trim()) params.set('search', search.trim());
      if (roleFilter) params.set('role', roleFilter);
      if (statusFilter) params.set('isActive', statusFilter);
      params.set('limit', '500');

      const response = await axios.get(`${API_BASE_URL}/admin/employees?${params.toString()}`);
      setEmployees(response.data?.data || []);
      setTotal(response.data?.total || 0);
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to fetch employees');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(fetchEmployees, 250);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, roleFilter, statusFilter]);

  useEffect(() => {
    if (!canSeeRoles) return;
    axios
      .get(`${API_BASE_URL}/admin/roles`)
      .then((res) => setRoles(res.data?.data || []))
      .catch(() => setRoles([]));
  }, [canSeeRoles]);

  const handleToggleActive = async (employee) => {
    setBusyId(employee.id);
    setError('');
    try {
      await axios.put(`${API_BASE_URL}/admin/employees/${employee.id}`, { isActive: !employee.isActive });
      await fetchEmployees();
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to update employee');
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (employee) => {
    if (!window.confirm(`Delete the account "${employee.username}"? This cannot be undone.`)) return;
    setBusyId(employee.id);
    setError('');
    try {
      await axios.delete(`${API_BASE_URL}/admin/employees/${employee.id}`);
      await fetchEmployees();
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to delete employee');
    } finally {
      setBusyId(null);
    }
  };

  const activeCount = employees.filter((e) => e.isActive).length;

  const pagination = usePagination(employees, { noun: 'employees' });

  return (
    <Layout>
      <div className="container">
        <div className="access-header">
          <div>
            <h1>Employees</h1>
            <p>Staff accounts for the admin panel and the role each one is given.</p>
          </div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <div className="access-stats">
              <span className="access-badge">Total: {total}</span>
              <span className="access-badge">Active: {activeCount}</span>
            </div>
            {canCreate && (
              <Link to="/sy-admin/employees/new" className="btn btn-primary">
                <span className="btn-icon">+</span> Add Employee
              </Link>
            )}
          </div>
        </div>

        {error && <div className="error-message">{error}</div>}

        <div className="access-toolbar">
          <input
            className="access-input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name / username / email…"
          />
          {canSeeRoles && (
            <select className="access-input" value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)}>
              <option value="">All Roles</option>
              {roles.map((role) => (
                <option key={role._id} value={role._id}>{role.name}</option>
              ))}
            </select>
          )}
          <select className="access-input" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="">All Status</option>
            <option value="true">Active</option>
            <option value="false">Inactive</option>
          </select>
        </div>

        {loading ? (
          <div className="access-table-container">
            <table className="access-table">
              <tbody>
                {[1, 2, 3, 4].map((i) => (
                  <tr key={i}>
                    <td colSpan={6}>
                      <div className="skeleton skeleton-text" style={{ width: '60%', height: 16 }} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : employees.length === 0 ? (
          <div className="empty-state">
            <span className="empty-state-icon">🧑‍💼</span>
            <h2>No employees found</h2>
            <p>Create an account and assign a role to give someone scoped access.</p>
            {canCreate && (
              <Link to="/sy-admin/employees/new" className="btn btn-primary">Add Your First Employee</Link>
            )}
          </div>
        ) : (
          <div className="access-table-container">
            <table className="access-table">
              <thead>
                <tr>
                  <th>Employee</th>
                  <th>Contact</th>
                  <th>Role</th>
                  <th>Last Login</th>
                  <th>Status</th>
                  <th style={{ textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {pagination.items.map((employee) => {
                  const isSelf = user && String(user.id) === String(employee.id);
                  return (
                    <tr key={employee.id}>
                      <td>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                          <span className="access-name">
                            {employee.name || employee.username}
                            {isSelf && <span className="access-muted"> (you)</span>}
                          </span>
                          <span className="access-muted">@{employee.username}</span>
                        </div>
                      </td>
                      <td>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                          <span>{employee.email || '—'}</span>
                          <span className="access-muted">{employee.phone || ''}</span>
                        </div>
                      </td>
                      <td>
                        {employee.isSuperAdmin ? (
                          <span className="access-tag access-tag-super">Super Admin</span>
                        ) : employee.role ? (
                          <span className="access-tag access-tag-role">{employee.role.name}</span>
                        ) : (
                          <span className="access-tag access-tag-none">No role</span>
                        )}
                      </td>
                      <td className="access-muted">{formatDateTime(employee.lastLogin)}</td>
                      <td>
                        {canUpdate && !isSelf ? (
                          <label className="status-toggle-label">
                            <div className="toggle-switch">
                              <input
                                type="checkbox"
                                className="toggle-input"
                                checked={employee.isActive}
                                disabled={busyId === employee.id}
                                onChange={() => handleToggleActive(employee)}
                              />
                              <span className={`toggle-slider ${busyId === employee.id ? 'loading' : ''}`} />
                            </div>
                            <span className={`status-text ${employee.isActive ? 'status-active-text' : 'status-inactive-text'}`}>
                              {employee.isActive ? 'Active' : 'Inactive'}
                            </span>
                          </label>
                        ) : (
                          <span className={`access-tag ${employee.isActive ? 'access-tag-active' : 'access-tag-inactive'}`}>
                            {employee.isActive ? 'Active' : 'Inactive'}
                          </span>
                        )}
                      </td>
                      <td>
                        <div className="access-actions">
                          <button
                            onClick={() => navigate(`/sy-admin/employees/edit/${employee.id}`)}
                            className="btn btn-edit btn-icon-only"
                            title={canUpdate ? 'Edit employee' : 'View employee'}
                          >
                            <span className="btn-icon">{canUpdate ? '✏️' : '👁️'}</span>
                          </button>
                          {canDelete && !isSelf && (
                            <button
                              onClick={() => handleDelete(employee)}
                              className="btn btn-delete btn-icon-only"
                              title="Delete employee"
                              disabled={busyId === employee.id}
                            >
                              <span className="btn-icon">🗑️</span>
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <Pagination {...pagination.props} />
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Employees;
